<template>
    <b-nav-item-dropdown variant="link" toggle-class="text-decoration-none" no-caret right>
        <template v-slot:button-content>
            <fa-icon-badge
                :icon="['fas', 'bell']"
                title="Notifications & Alerts"
                :show-badge="
                    notificationList.length > 0 ||
                    failedTransactionsCount > 0 ||
                    undergoingResolutionTransactionsCount > 0
                "
            ></fa-icon-badge>
            <span class="sr-only">Notifications & Alerts</span>
        </template>

        <b-dropdown-header>
            <div style="font-weight: 700; font-size: 1.1rem; min-width: 300px; text-transform: uppercase">
                Notifications & Alerts
            </div>
        </b-dropdown-header>

        <div style="margin-bottom: 0.5rem">
            <div
                style="
                    max-height: 30rem;
                    overflow-y: scroll;
                    border-radius: 0px;
                    border-top: 1px solid rgba(0, 0, 0, 0.125);
                    border-bottom: 1px solid rgba(0, 0, 0, 0.125);
                    border-left: 0px;
                    border-right: 0px;
                "
                v-if="
                    notificationList.length > 0 ||
                    failedTransactionsCount > 0 ||
                    undergoingResolutionTransactionsCount > 0
                "
            >
                <div
                    v-if="failedTransactionsCount > 0 || undergoingResolutionTransactionsCount > 0"
                    style="padding: 1.1rem"
                    :style="notificationList.length > 0 ? 'border-bottom: 1px solid rgba(0, 0, 0, 0.125)' : ''"
                >
                    <div class="d-flex w-100 justify-content-between">
                        <h5 class="mb-1">Failed Transaction Alert</h5>
                    </div>
                    <div>
                        You have
                        <span v-if="failedTransactionsCount">
                            <strong>{{ failedTransactionsCount }}</strong>
                            <span v-if="failedTransactionsCount"> transaction{{ failedTransactionsPlural }}</span>
                            unresolved<span v-if="!undergoingResolutionTransactionsCount">.</span>
                        </span>
                        <span v-if="undergoingResolutionTransactionsCount">
                            <span v-if="failedTransactionsCount"> and</span>
                            <strong>{{ undergoingResolutionTransactionsCount }}</strong>
                            <span v-if="undergoingResolutionTransactionsCount">
                                transaction{{ failedTransactionsPlural }}</span
                            >
                            undergoing resolution.</span
                        >
                    </div>
                    <div class="d-flex mt-3 mb-1 w-100 justify-content-center">
                        <b-button variant="primary" :href="failedTransactionsHref">Review Failed Transactions</b-button>
                    </div>
                </div>
                <notification-message
                    v-for="(item, index) in notificationList"
                    :key="index"
                    :item="item"
                    :index="index"
                    style="padding: 1.1rem"
                ></notification-message>
            </div>

            <div
                v-if="notificationList.length <= 0"
                :style="
                    failedTransactionsCount > 0 || undergoingResolutionTransactionsCount > 0
                        ? ''
                        : 'border-top: 1px solid rgba(0, 0, 0, 0.125)'
                "
                style="
                    min-width: 300px;
                    border-bottom: 1px solid rgba(0, 0, 0, 0.125);
                    padding-top: 0.5rem;
                    padding-bottom: 0.5rem;
                "
            >
                <b-dropdown-header>
                    <div style="font-weight: 500; font-size: 1rem">No Unread Notifications</div>
                </b-dropdown-header>
            </div>
        </div>

        <b-dropdown-group class="lx-header-account-menu">
            <b-dropdown-item href="/app/notification/"> View All Notifications </b-dropdown-item>
            <b-dropdown-item @click="markAllAsRead()" v-if="notificationList.length > 0">
                Mark All As Read
            </b-dropdown-item>
            <div v-if="this.$route.path != '/app/notification/devices/'">
                <b-dropdown-item
                    @click="requestPushNotifications"
                    v-if="showEnablePushNotification && isNotificationSupported"
                >
                    Enable Push Notifications
                </b-dropdown-item>
                <b-dropdown-item
                    @click="disablePushNotifications"
                    v-if="!showEnablePushNotification && isNotificationSupported"
                >
                    Disable Push Notifications
                </b-dropdown-item>
            </div>
        </b-dropdown-group>
    </b-nav-item-dropdown>
</template>

<script>
    import FaIconBadge from '../../generic/fa-icon-badge/fa-icon-badge.vue';
    import { mapGetters, mapMutations } from 'vuex';
    import { requestNotifications } from '../../../utils/firebase.js';
    import API from '../../../api';
    import { FCM_DEVICE_ENDPOINT, NOTIFICATION_ENDPOINT } from '../../../endpoints';
    import { NOTIFICATION_DROPDOWN_MAX } from '../../../constants';
    import NotificationMessage from './notification-message.vue';

    export default {
        name: 'HeaderNotification',
        components: {
            FaIconBadge,
            NotificationMessage,
        },
        computed: {
            ...mapGetters('header', [
                'notificationList',
                'isNotificationSupported',
                'failedTransactionsHref',
                'failedTransactionsCount',
                'undergoingResolutionTransactionsCount',
            ]),
            showEnablePushNotification() {
                if (this.currentToken() === null) {
                    return true;
                } else {
                    const devicesList = this.deviceList();
                    const token = this.currentToken();
                    const device = devicesList.find((device) => device.registration_id === token);
                    if (!device) {
                        return true;
                    } else {
                        return !device?.active;
                    }
                }
            },
            failedTransactionsPlural() {
                return this.failedTransactionsCount > 1 || this.undergoingResolutionTransactionsCount > 1 ? 's' : '';
            },
        },
        methods: {
            ...mapMutations('header', ['setDevices', 'setToken', 'setNotificationList']),
            ...mapGetters('header', ['deviceList', 'currentToken', 'fcmClientConfig', 'fcmVapidKey']),
            async requestPushNotifications() {
                const token = await requestNotifications(this.fcmClientConfig(), this.fcmVapidKey(), (title, body) => {
                    this.errorToast({ text: body, title: title });
                    setTimeout(async () => {
                        const notificationList = await API.get(`${NOTIFICATION_ENDPOINT()}notifications/`, {
                            params: {
                                offset: 0,
                                limit: NOTIFICATION_DROPDOWN_MAX,
                                sort: 'date',
                                desc: true,
                                is_read: false,
                            },
                        });
                        this.setNotificationList(notificationList.data);
                    }, 1000);
                });
                this.setToken(token);
                const devicesList = this.deviceList();
                const device = devicesList.find((device) => device.registration_id === token);
                let deviceName = navigator.userAgent;
                if (device) {
                    deviceName = device.name;
                }
                await API.post(`${FCM_DEVICE_ENDPOINT()}`, {
                    registration_id: token,
                    type: 'web',
                    name: deviceName,
                    active: true,
                });
                const list = await API.get(`${FCM_DEVICE_ENDPOINT()}`);
                this.setDevices(list.data);
            },
            async disablePushNotifications() {
                const token = this.currentToken();
                await API.post(`${FCM_DEVICE_ENDPOINT()}`, {
                    registration_id: token,
                    type: 'web',
                    active: false,
                });
                this.setToken(null);
                const list = await API.get(`${FCM_DEVICE_ENDPOINT()}`);
                this.setDevices(list.data);
            },

            async markAllAsRead() {
                await API.patch(`${NOTIFICATION_ENDPOINT()}notifications_set_read_all/`);
                const notificationList = await API.get(`${NOTIFICATION_ENDPOINT()}notifications/`, {
                    params: {
                        offset: 0,
                        limit: NOTIFICATION_DROPDOWN_MAX,
                        sort: 'date',
                        desc: true,
                        is_read: false,
                    },
                });
                this.setNotificationList(notificationList.data);
            },
        },
    };
</script>

<style lang="scss" scoped>
    @import '~bootstrap/scss/_functions.scss';
    @import '~bootstrap/scss/_variables.scss';
    @import '~bootstrap/scss/mixins/_breakpoints.scss';

    @include media-breakpoint-down(md) {
        .dropdown {
            position: static;
        }

        :deep(.dropdown-menu) {
            position: absolute !important;
            right: 0;
            width: calc(100vw - 2rem);
            overflow: hidden;
        }
    }

    @include media-breakpoint-up(lg) {
        :deep(.dropdown-menu) {
            max-height: 80vh;
            overflow: auto;
        }
    }

    .lx-header-account-menu {
        padding: 0 0.5rem;
    }

    .lx-header-account-menu :deep(.dropdown-item .fa-sm) {
        margin-right: 0.5rem;
        vertical-align: initial;
    }

    /*
      Overwriting bootstrap style
     */
    :deep(.nav-link) {
        color: #ffffff !important;
    }

    :deep(.nav-link:hover,.nav-link:active) {
        color: rgba(255, 255, 255, 0.75) !important;
    }

    :deep(.nav-link) {
        padding-left: 0.5rem !important;
    }

    :deep(.dropdown-menu) {
        border-radius: 4px !important;
    }

    :deep(.dropdown-item) {
        padding: 0.5rem;
    }

    :deep(.dropdown-item:hover,.dropdown-item:active) {
        background-color: #4285f4;
        border-radius: 0.125rem;
        -webkit-box-shadow: 0 5px 11px 0 rgba(0, 0, 0, 0.18), 0 4px 15px 0 rgba(0, 0, 0, 0.15);
        box-shadow: 0 5px 11px 0 rgba(0, 0, 0, 0.18), 0 4px 15px 0 rgba(0, 0, 0, 0.15);
        color: #ffffff !important;
    }

    :deep(.dropdown-header) {
        padding: 0.5rem 1rem;
    }
</style>
